import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Splits a price line such as "Essential €249,99" into package and amount,
 * so stacked price lines can show them as aligned columns.
 */
export function splitPackagePrice(line: string): [string, string] {
  const m = /^(.*?)\s*(€.*)$/.exec(line);
  return m ? [m[1], m[2]] : [line, ""];
}

/**
 * E-mail check shared by a form and its server action, so the browser never
 * lets through an address the server then rejects. The same pattern as zod's
 * `.email()`: ASCII only, so an IDN domain ("müller.de") counts as invalid.
 */
export const EMAIL_PATTERN =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-\.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;

/**
 * Formats integers with thousands separators identically in Node SSR and the
 * browser. Do not use `Number#toLocaleString` here: ICU differences between
 * runtimes produce different separator characters and cause hydration errors.
 */
export function formatIntegerWithSeparators(
  n: number,
  separator: "'" | "." | "," | "\u00A0",
): string {
  const rounded = Math.round(Math.abs(n));
  const s = String(rounded);
  const parts: string[] = [];
  let rest = s;
  while (rest.length > 3) {
    parts.unshift(rest.slice(-3));
    rest = rest.slice(0, -3);
  }
  if (rest) parts.unshift(rest);
  const formatted = parts.join(separator);
  return n < 0 ? `-${formatted}` : formatted;
}

export function formatPrice(amount: number, currency: "EUR" | "CHF" = "EUR") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}
